import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { authAPI } from '../../services/api';
import JourneyLayout from '../../components/JourneyLayout';
import { eventToRouteMap } from '../../Utils/constants';

function PANDetails() {
  const [step, setStep] = useState('pan-input'); // 'pan-input' or 'details-form'
  const [panNumber, setPanNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [panData, setPanData] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Form fields
  const [formData, setFormData] = useState({
    fatherName: '',
    email: '',
    employmentType: '',
    monthlyIncome: '',
    pincode: '',
  });

  const employmentOptions = [
    { value: 'salaried', label: 'Salaried', id: 1 },
    { value: 'self_employed', label: 'Self Employed', id: 2 },
  ];

  const navigate = useNavigate();
  const profileId = localStorage.getItem('profile_id') || '';
  const autoSubmitRef = useRef(false);

  useEffect(() => {
    const checkLoginStatus = async () => {
      setCheckingAuth(true);
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('authtoken');
      
      if (!token || !profileId) {
        navigate('/apply-now');
        return;
      }

      // Fetch dashboard data
      try {
        const response = await authAPI.getDashboardData(profileId);
        
        if (response.Status === 1 && response.Data?.profile_details) {
          const profileData = response.Data.profile_details;
          
          // Check if PAN, Aadhaar (last 4 digits), and DOB are already verified
          const hasPan = profileData.pancard && profileData.pancard.trim() !== '';
          const hasAadhaar = profileData.aadhaar_no && profileData.aadhaar_no.trim() !== '';
          const hasDob = profileData.dob && profileData.dob.trim() !== '';
          
          // If all three are present, KYC is verified - skip to details form
          if (hasPan && hasAadhaar && hasDob) {
            // Map gender: 1 -> "male", 2 -> "female", otherwise -> ""
            const mapGender = (gender) => {
              if (gender === 1) return 'Male';
              if (gender === 2) return 'Female';
              return '';
            };
            
            // Set PAN data from dashboard response
            setPanData({
              full_name: response.Data.full_name || profileData.first_name || '',
              dob: profileData.dob,
              aadhaar_number: profileData.aadhaar_no,
              gender: mapGender(profileData.gender),
              pancard: profileData.pancard,
            });
            
            // Set form data from dashboard response
            setFormData((prev) => ({
              ...prev,
              fatherName: profileData.father_name || '',
              email: profileData.personal_email || '',
            }));
            
            // Set PAN number for display
            setPanNumber(profileData.pancard || '');
            
            // Skip to details form
            setStep('details-form');
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkLoginStatus();
  }, [profileId, navigate]);

  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ lat: '', lng: '' });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude.toString(),
            lng: position.coords.longitude.toString(),
          });
        },
        (error) => {
          console.warn('Geolocation error:', error);
          resolve({ lat: '', lng: '' });
        }
      );
    });
  };

  const handlePANChange = (e) => {
    let input = e.target.value.toUpperCase();
    
    // Remove all special characters, spaces, and invalid characters
    input = input.replace(/[^A-Z0-9]/g, '');
    
    // Enforce PAN format: 5 alphabets + 4 digits + 1 alphabet
    let formattedValue = '';
    
    for (let i = 0; i < input.length && formattedValue.length < 10; i++) {
      const char = input[i];
      const currentPos = formattedValue.length;
      
      if (currentPos < 5) {
        // Positions 0-4: Must be alphabets only
        if (/[A-Z]/.test(char)) {
          formattedValue += char;
        }
        // Ignore digits at this position
      } else if (currentPos < 9) {
        // Positions 5-8: Must be digits only
        if (/[0-9]/.test(char)) {
          formattedValue += char;
        }
        // Ignore alphabets at this position
      } else if (currentPos === 9) {
        // Position 9: Must be an alphabet
        if (/[A-Z]/.test(char)) {
          formattedValue += char;
        }
        // Ignore digits at this position
      }
    }
    
    setPanNumber(formattedValue);
    
    // Clear errors while user is typing
    if (formattedValue.length > 0) {
      setErrors({ ...errors, pan: '' });
    }
  };

  // Auto-submit when PAN is complete and valid (similar to OTP page)
  // Only run when step is 'pan-input' to prevent multiple calls
  useEffect(() => {
    // Don't run if we're already on details form or checking auth
    if (step !== 'pan-input' || checkingAuth) {
      autoSubmitRef.current = false;
      return;
    }

    const isValidPAN = panNumber.length === 10 && /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panNumber);
    
    if (isValidPAN && !loading && !autoSubmitRef.current && profileId) {
      autoSubmitRef.current = true;
      // Call handlePANSubmit directly
      const submitPAN = async () => {
        const newErrors = {};

        if (!panNumber) {
          newErrors.pan = 'PAN number is required';
        } else if (panNumber.length !== 10) {
          newErrors.pan = 'Invalid PAN - Please enter a complete 10-character PAN number';
        } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panNumber)) {
          newErrors.pan = 'Invalid PAN - Format must be 5 letters, 4 digits, and 1 letter (e.g., ABCDE1234F)';
        }

        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          autoSubmitRef.current = false;
          return;
        }

        setLoading(true);
        setErrors({});

        try {
          const location = await getCurrentLocation();
          console.log("location in try", location);

          const response = await authAPI.verifyPAN({
            pancard: panNumber,
            profile_id: profileId,
            monthly_income: '',
            geo_lat: location.lat,
            geo_long: location.lng,
          });
          console.log("response", response);
          if (response.Status === 1) {
            setPanData(response.Data);
            setFormData((prev) => ({
              ...prev,
              fatherName: response.Data.father_name || '',
              email: response.Data.email || '',
            }));
            setStep('details-form');
            autoSubmitRef.current = false;
          } else {
            // Clear PAN input on error
            setPanNumber('');
            autoSubmitRef.current = false;
            setErrors({ pan: response.Message || 'PAN verification failed. Please try again.' });
          }
        } catch (error) {
          // Clear PAN input on error
          setPanNumber('');
          autoSubmitRef.current = false;
          setErrors({ pan: error.message || 'Failed to verify PAN. Please try again.' });
        } finally {
          setLoading(false);
        }
      };

      submitPAN();
    } else if (!isValidPAN && panNumber.length < 10) {
      // Reset ref when PAN is not valid and user is still typing
      autoSubmitRef.current = false;
    }
  }, [panNumber, loading, profileId, step, checkingAuth]);

  const handlePANSubmit = async (e) => {
    e.preventDefault();
    autoSubmitRef.current = false;
    const newErrors = {};

    if (!panNumber) {
      newErrors.pan = 'PAN number is required';
    } else if (panNumber.length !== 10) {
      newErrors.pan = 'Invalid PAN - Please enter a complete 10-character PAN number';
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panNumber)) {
      newErrors.pan = 'Invalid PAN - Format must be 5 letters, 4 digits, and 1 letter (e.g., ABCDE1234F)';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const location = await getCurrentLocation();
      console.log("location in try", location);

      const response = await authAPI.verifyPAN({
        pancard: panNumber,
        profile_id: profileId,
        monthly_income: '',
        geo_lat: location.lat,
        geo_long: location.lng,
      });
      console.log("response", response);
      if (response.Status === 1) {
        setPanData(response.Data);
        setFormData((prev) => ({
          ...prev,
          fatherName: response.Data.father_name || '',
          email: response.Data.email || '',
        }));
        setStep('details-form');
      } else {
        // Clear PAN input on error
        setPanNumber('');
        setErrors({ pan: response.Message || 'PAN verification failed. Please try again.' });
      }
    } catch (error) {
      // Clear PAN input on error
      setPanNumber('');
      setErrors({ pan: error.message || 'Failed to verify PAN. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply input restrictions based on field type
    if (name === 'fatherName') {
      // Only allow alphabets and spaces
      processedValue = value.replace(/[^A-Za-z\s]/g, '');
    } else if (name === 'email') {
      // Allow email characters: letters, numbers, @, ., _, -, +
      processedValue = value.replace(/[^A-Za-z0-9@._+-]/g, '');
    } else if (name === 'monthlyIncome') {
      // Only allow numbers
      processedValue = value.replace(/\D/g, '');
    } else if (name === 'pincode') {
      // Only allow numbers, max 6 digits
      processedValue = value.replace(/\D/g, '').slice(0, 6);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
    setErrors({ ...errors, [name]: '', api: '' });

    // If self_employed is selected, navigate to thank-you page
    if (name === 'employmentType' && value === 'self_employed') {
      navigate('/thank-you');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.fatherName.trim()) {
      newErrors.fatherName = 'Father\'s name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.employmentType) {
      newErrors.employmentType = 'Employment type is required';
    }
    if (!formData.monthlyIncome.trim()) {
      newErrors.monthlyIncome = 'Monthly income is required';
    } else if (!/^\d+$/.test(formData.monthlyIncome.trim())) {
      newErrors.monthlyIncome = 'Please enter a valid monthly income';
    }
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode.trim())) {
      newErrors.pincode = 'Please enter a valid 6-digit pincode';
    }

    const selectedEmployment = employmentOptions.find(
      (option) => option.value === formData.employmentType
    );

    if (!selectedEmployment) {
      newErrors.employmentType = 'Please select a valid employment type';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!panData?.dob) {
      setErrors({ api: 'Missing date of birth information. Please verify PAN again.' });
      return;
    }

    setDetailsLoading(true);
    setErrors({});

    try {
      const location = await getCurrentLocation();
      console.log("location", location);

      const payload = {
        profile_id: profileId,
        dob: panData.dob,
        income_type_id: selectedEmployment.id,
        salary_mode_id: '',
        monthly_income: Number(formData.monthlyIncome.trim()),
        personal_email: formData.email.trim(),
        residence_pincode: formData.pincode.trim(),
        father_name: formData.fatherName.trim(),
        geo_lat: location.lat,
        geo_long: location.lng,
      };
      console.log("payload", payload);
      const response = await authAPI.submitPersonalDetails(payload);

      if (response.Status === 1) {
        const nextEvent = response.Data?.next_step;
        if (nextEvent && eventToRouteMap[nextEvent]) {
          navigate(eventToRouteMap[nextEvent]);
        } else {
          navigate('/my-dashboard/upload-picture');
        }
      } else {
        setErrors({ api: response.Message || 'Failed to submit details. Please try again.' });
      }
    } catch (error) {
      setErrors({ api: error.message || 'Failed to submit details. Please try again.' });
    } finally {
      setDetailsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatGender = (gender) => {
    if (!gender) return '';
    return gender.charAt(0).toUpperCase() + gender.slice(1);
  };

  if (checkingAuth) {
    return (
      <JourneyLayout journeyStep={2}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-theme" />
        </div>
      </JourneyLayout>
    );
  }

  if (step === 'pan-input') {
    return (
      <JourneyLayout journeyStep={2}>
        <button
          onClick={() => navigate('/otp')}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-theme mb-6 transition-colors bg-transparent border-none p-0"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </button>

        <div className="text-center space-y-3 mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-theme/10 border border-theme/20 text-xs tracking-[0.3em] uppercase text-theme font-semibold">
            <Building2 className="h-4 w-4" />
            Snap Paisa
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Confirm PAN Details</h1>
          <p className="text-gray-600">
            Enter your PAN number to pull data securely from government databases.
          </p>
        </div>

        <form onSubmit={handlePANSubmit} className="space-y-6">
          <div>
            <label htmlFor="pan" className="block text-sm font-semibold tracking-wide text-gray-700 mb-3">
              PAN Number
            </label>
            <input
              type="text"
              id="pan"
              value={panNumber}
              onChange={handlePANChange}
              placeholder="ABCDE1234F"
              maxLength="10"
              autoComplete="off"
              className={`w-full px-4 py-5 rounded-2xl bg-gray-50 border-2 text-center text-lg sm:text-xl md:text-2xl font-bold tracking-[0.4em] sm:tracking-[0.5em] md:tracking-[0.6em] uppercase ${
                errors.pan ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
              } focus:outline-none transition-all text-gray-900`}
            />
            {errors.pan && (
              <p className="mt-2 text-xs text-red-500 font-medium">{errors.pan}</p>
            )}
          </div>

          {!loading && (
            <div className="text-center text-xs uppercase tracking-[0.3em] text-gray-500 font-medium">
              Auto verifying once PAN is valid
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-theme to-theme2 py-4 text-base font-semibold shadow-lg text-white">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Verifying PAN...</span>
            </div>
          )}

          <button
            type="submit"
            onClick={handlePANSubmit}
            className="hidden"
            aria-hidden="true"
          >
            Submit
          </button>
        </form>
      </JourneyLayout>
    );
  }

  return (
    <JourneyLayout journeyStep={2}>
      <button
        onClick={() => setStep('pan-input')}
        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-theme mb-6 transition-colors bg-transparent border-none p-0"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back</span>
      </button>

      <div className="grid gap-8 lg:grid-cols-2 mb-8">
        <div className="bg-gradient-to-br from-theme/5 to-theme2/5 border-2 border-theme/20 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm text-theme font-semibold">
            <CheckCircle className="h-5 w-5" />
            Verified via PAN
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Full Name</p>
              <p className="text-lg font-semibold text-gray-900">{panData?.full_name || '-'}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">DOB</p>
                <p className="text-base font-semibold text-gray-900">
                  {panData?.dob ? formatDate(panData.dob) : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Gender</p>
                <p className="text-base font-semibold text-gray-900">
                  {panData?.gender ? formatGender(panData.gender) : '-'}
                </p>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Aadhaar (last 4)</p>
              <p className="text-base font-semibold text-gray-900">{panData?.aadhaar_number || '-'}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">Confirm Your Details</h2>
          <p className="text-gray-600">
            Fill the remaining fields to complete your Snap Paisa onboarding.
          </p>
        </div>
      </div>

      <form onSubmit={handleFormSubmit} className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Father&apos;s Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="fatherName"
              name="fatherName"
              value={formData.fatherName}
              onChange={handleFormChange}
              placeholder="Enter your father's full name"
              autoComplete="off"
              maxLength={50}
              className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 ${
                errors.fatherName ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
              } text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all`}
            />
            {errors.fatherName && (
              <p className="text-xs text-red-500 font-medium">{errors.fatherName}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleFormChange}
              placeholder="example@email.com"
              autoComplete="off"
              className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 ${
                errors.email ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
              } text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all`}
            />
            {errors.email && (
              <p className="text-xs text-red-500 font-medium">{errors.email}</p>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1 space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Employment Type <span className="text-red-500">*</span>
            </label>
            <select
              id="employmentType"
              name="employmentType"
              value={formData.employmentType}
              onChange={handleFormChange}
              className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 ${
                errors.employmentType ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
              } text-gray-900 focus:outline-none transition-all`}
            >
              <option value="" className="text-gray-900">
                Choose option
              </option>
              <option value="salaried" className="text-gray-900">Salaried</option>
              <option value="self_employed" className="text-gray-900">Self Employed</option>
            </select>
            {errors.employmentType && (
              <p className="text-xs text-red-500 font-medium">{errors.employmentType}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Monthly Income <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="monthlyIncome"
              name="monthlyIncome"
              value={formData.monthlyIncome}
              onChange={handleFormChange}
              placeholder="₹"
              autoComplete="off"
              inputMode="numeric"
              maxLength={10}
              className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 ${
                errors.monthlyIncome ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
              } text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all`}
            />
            {errors.monthlyIncome && (
              <p className="text-xs text-red-500 font-medium">{errors.monthlyIncome}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Pincode <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="pincode"
              name="pincode"
              value={formData.pincode}
              onChange={handleFormChange}
              placeholder="6 digit code"
              maxLength="6"
              autoComplete="off"
              inputMode="numeric"
              className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 ${
                errors.pincode ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
              } text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all`}
            />
            {errors.pincode && (
              <p className="text-xs text-red-500 font-medium">{errors.pincode}</p>
            )}
          </div>
        </div>

        {errors.api && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 text-sm text-red-700">
            {errors.api}
          </div>
        )}

        <button
          type="submit"
          disabled={detailsLoading}
          className="w-full bg-gradient-to-r from-theme to-theme2 text-white px-6 py-4 rounded-2xl font-semibold text-lg flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 transition-all"
        >
          {detailsLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </button>
      </form>
    </JourneyLayout>
  );
}

export default PANDetails;

